// Single source of truth for the Information Edge label.
//
// This lived in two places (TraderCard and CongressMemberProfile) and drifted:
// both read bullish_transactions.unified_score_ci_lower, a bootstrapped Sortino
// bound, while the leaderboard ranked on alpha_lower_bound from the
// empirical-Bayes posterior. Different statistics on different scales, so they
// disagreed exactly where records are thin -- the bootstrap returns null below
// a minimum sample, which let a 3-trade member rank 5th on the board while
// their own card read "Insufficient Data".
//
// Both now read alpha_prob_positive: P(true alpha > 0) under the same
// posterior the board ranks on, so label and rank cannot contradict.
//
// Why a probability rather than a threshold on the lower bound: a bound
// conflates confidence with effect size. Requiring "95% sure alpha exceeds 8
// points" labels almost nobody and understates a small edge backed by a long
// record -- 844 trades at +3.4% is near-certain but unremarkable on a
// magnitude bar. P(alpha > 0) asks what the label actually claims.

// Below this many trades we make no evidentiary claim. The posterior returns a
// number at any n because the prior fills the gap, but a 3-trade record should
// not carry a label however confident the arithmetic is.
export const MIN_TRADES = 5;

export const EDGE_THRESHOLDS = { strong: 0.90, likely: 0.75, possible: 0.60 };

/**
 * @param {number|null} probPositive  member.alpha_prob_positive, 0-1
 * @param {number} tradeCount         bullish_transactions.count
 * @returns {{key: string, label: string}}
 */
export function getInfoEdge(probPositive, tradeCount) {
    if (probPositive == null || !(tradeCount >= MIN_TRADES)) {
        return { key: 'insufficient', label: 'Insufficient Data' };
    }
    if (probPositive >= EDGE_THRESHOLDS.strong) return { key: 'strong', label: 'Strong' };
    if (probPositive >= EDGE_THRESHOLDS.likely) return { key: 'likely', label: 'Likely' };
    if (probPositive >= EDGE_THRESHOLDS.possible) return { key: 'possible', label: 'Possible' };
    return { key: 'none', label: 'No Evidence' };
}

export const INFO_EDGE_TOOLTIP =
    "Confidence that this member's market-beating returns are real rather than " +
    'luck, after adjusting for how many trades back them up. A long record of ' +
    'modest gains scores higher than a handful of spectacular ones.';
